/**
 * Module dependencies.
 */

import { useBankAccounts } from 'app/hooks/requests/bank-accounts/use-bank-accounts';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CrudTemplate } from 'app/components/organisms/crud/form-types';
import { cn } from 'app/lib/utils';

/**
 * `Props` type.
 */

type Props = {
  className: string;
  children: JSX.Element;
  refetch: () => Promise<void>;
  isLoading: boolean;
  template: CrudTemplate;
};

/**
 * Export `Wrapper` component.
 */

export function Wrapper({ template, className, children, refetch }: Props): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams(template?.list?.defaultQueryString);
  const [updateList, setUpdateList] = useState<number>();
  const account = useMemo(() => {
    const result = searchParams.get('filter');
    const data = JSON.parse(result ?? '{}');

    return data.account;
  }, [searchParams]);

  const [activeKey, setActiveKey] = useState<string | undefined>(account ? String(account) : undefined);
  const { data } = useBankAccounts();
  const items = useMemo(
    () => [
      {
        key: String(-1),
        label: 'All'
      },
      ...(data?.results?.map(item => ({
        key: String(item.id),
        label: item.name
      })) ?? [])
    ],
    [data]
  );

  useEffect(() => {
    setSearchParams(searchParams => {
      const filter = searchParams.get('filter');
      const data = JSON.parse(filter ?? '{}');

      if (!activeKey || parseInt(activeKey, 10) < 0) {
        delete data.account;
      } else {
        data.account = parseInt(activeKey ?? '0', 10);
      }

      searchParams.set('filter', JSON.stringify(data));

      return searchParams;
    });
    setUpdateList(Date.now());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeKey, refetch]);

  const activeValue = activeKey && parseInt(activeKey, 10) >= 0 ? activeKey : String(-1);

  const listRef = useRef<HTMLDivElement>(null);
  const [indicator, setIndicator] = useState<{ left: number; width: number }>({ left: 0, width: 0 });

  // Slide/resize the underline to the active tab. Re-measured when the active
  // tab or the account list changes, and on resize.
  useLayoutEffect(() => {
    function measure() {
      const active = listRef.current?.querySelector<HTMLButtonElement>('[data-active="true"]');

      if (active) {
        setIndicator({ left: active.offsetLeft, width: active.offsetWidth });
      }
    }

    measure();

    window.addEventListener('resize', measure);

    return () => window.removeEventListener('resize', measure);
  }, [activeValue, items]);

  return (
    <div className={'-mx-4 overflow-hidden px-4'}>
      <div className={'-mx-4 mb-4 overflow-x-auto px-4'}>
        <div className={'relative flex min-w-max items-center gap-1 border-b border-border'} ref={listRef}>
          {items.map(item => {
            const active = item.key === activeValue;

            return (
              <button
                className={cn(
                  'relative whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors',
                  active ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                )}
                data-active={active}
                key={item.key}
                onClick={() => setActiveKey(item.key)}
                type={'button'}
              >
                {item.label}
              </button>
            );
          })}

          <span
            className={
              'pointer-events-none absolute -bottom-px h-0.5 rounded-full bg-primary transition-all duration-300 ease-out'
            }
            style={{ left: indicator.left, width: indicator.width }}
          />
        </div>
      </div>

      <div className={className} key={updateList}>
        {children}
      </div>
    </div>
  );
}
